import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import api from '../../utils/api';

const EditBranchDialog = ({ open, onClose, onSuccess, branch }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    status: 'active'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (branch) {
      setFormData({
        name: branch.name || '',
        address: branch.address || '',
        phone: branch.phone || '',
        email: branch.email || '',
        status: branch.status || 'active'
      });
    }
  }, [branch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Branch name is required';
    if (!formData.address.trim()) return 'Address is required';
    if (!formData.phone.trim()) return 'Phone number is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Invalid email format';
    if (!/^\+?[\d\s-]+$/.test(formData.phone)) return 'Invalid phone number format';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const response = await api.put(`/branches/${branch.id}`, formData);
      onSuccess(response.data.branch);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update branch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose}
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Edit Branch</DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="name"
              label="Branch Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleChange}
              disabled={loading || branch?.is_main_branch}
              helperText={branch?.is_main_branch ? "Main branch name cannot be changed" : ""}
            />

            <TextField
              name="address"
              label="Physical Address"
              fullWidth
              required
              multiline
              rows={2}
              value={formData.address}
              onChange={handleChange}
              disabled={loading}
            />

            <TextField
              name="phone"
              label="Phone Number"
              fullWidth
              required
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
              helperText="Include country code (e.g., +254...)"
            />

            <TextField
              name="email"
              label="Branch Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />

            <FormControl fullWidth>
              <InputLabel>Branch Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading || branch?.is_main_branch}
                label="Branch Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
              {branch?.is_main_branch && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Main branch cannot be deactivated
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditBranchDialog; 